import { API_URL, API_VERSION, SITE_URLS } from '@constants';
import { useData } from '@hooks/useData';
import { paths } from '../../types/api';
import { ApiError, useApiConfig } from '@hooks/useApiConfig';
import { useCustomNavigate } from '@hooks/useCustomNavigate';
import { getSessionId } from '@utils/session';

const ENDPOINT_URL = '/str-stores/{uuid}';

const ENDPOINT_URL_WITH_VERSION = `${API_VERSION}${ENDPOINT_URL}` as const;
type url = typeof ENDPOINT_URL_WITH_VERSION;

export interface GetStoreDetailSuccessResponse {
    data: paths[url]['get']['responses']['200']['content']['application/json'];
}

/**
 * Requests endpoint to get store by uuid.
 */
export const useGetStoreDetail = () => {
    const { navigate } = useCustomNavigate();
    const { getHeaders, getCommonParameters, handleCommonErrors } = useApiConfig();
    const { setStoreDetail, setIsLoadingStoreDetail } = useData();

    const request = async (uuid: string) => {
        setIsLoadingStoreDetail(true);

        const sessionId = getSessionId();

        try {
            const commonQueryString = new URLSearchParams(getCommonParameters()).toString();
            const sessionQueryString = `session_id=${encodeURIComponent(sessionId)}`;
            const fullQueryString = `${commonQueryString}&${sessionQueryString}`;

            const response = await fetch(`${API_URL}${ENDPOINT_URL.replace('{uuid}', uuid)}?${fullQueryString}`, {
                method: 'GET',
                headers: getHeaders(),
            });

            if (!response.ok) {
                const json = await response.json();
                const error = new Error(json.error || '') as ApiError;
                error.statusCode = response.status;
                throw error;
            }

            const data: GetStoreDetailSuccessResponse = await response.json();

            setStoreDetail(data);
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } catch (error: any) {
            handleCommonErrors(error);

            if (error.statusCode === 404) {
                // redirect to 404 page
                navigate(`/${SITE_URLS.pageNotFound}`);
            }
        } finally {
            setIsLoadingStoreDetail(false);
        }
    };

    return { request };
};
